import {
    DOCUMENT_TEMPLATE_DOC_TYPES,
    type DocumentTemplateDocType,
} from "@/constants/documentTemplate.constants"
import { FREIGHT_DASHBOARD_MODE_TABS } from "./windows/freightDashboard/freightDashboard.utils"
import type { GlyphName } from "@/components/desktop/ui"
import { isRecord } from "@/utils/typeGuards.utils"

export const WINDOW_TYPES = [
    "wilson",
    "comms",
    "settings",
    "explorer",
    "document",
    "tasks",
    "todos",
    "setup",
    "onboarding",
    "diagramViewer",
    "reports",
    "report",
    "meetWilson",
    "knowledgeNote",
    "knowledgeDoc",
    "template",
    "warehouseScheduling",
    "skill",
    "freightDashboard",
    "cartagePay",
] as const

export type WindowType = (typeof WINDOW_TYPES)[number]

export const WINDOW_OPEN_SOURCES = [
    "dock",
    "shelf",
    "phoneBar",
    "spotlight",
    "url",
    "crossWindow",
    "desktopIcon",
    "autoOpen",
    "explorer",
] as const

export type WindowOpenSource = (typeof WINDOW_OPEN_SOURCES)[number]

export const LEGACY_WINDOW_TYPES = [
    "mail",
    "phone",
    "messages",
    "tables",
    "knowledgeNotes",
    "knowledgeDocs",
    "templates",
    "memories",
    "rules",
    "skills",
] as const

export const RECORD_WINDOW_TYPES: ReadonlySet<WindowType> = new Set([
    "document",
    "report",
    "template",
    "knowledgeNote",
    "knowledgeDoc",
    "diagramViewer",
    "skill",
])

export type WindowDisplayState = "normal" | "minimized"

export interface WindowState {
    id: string
    type: WindowType
    title: string
    displayState: WindowDisplayState
    zIndex: number
    data?: Record<string, unknown>
}

export interface DockAppConfig {
    id: string
    type: WindowType
    label: string
    iconSrc?: string
    darkIconSrc?: string
    fallbackIcon?: GlyphName
}

export interface WindowManagerState {
    windows: WindowState[]
    activeWindowId: string | null
}

export interface WindowManagerActions {
    openWindow: (
        type: WindowType | (typeof LEGACY_WINDOW_TYPES)[number],
        options?: {
            title?: string
            data?: Record<string, unknown>
            source?: WindowOpenSource
        }
    ) => string
    closeWindow: (id: string) => void
    closeAllWindows: () => void
    focusWindow: (id: string) => void
    updateTitle: (id: string, title: string) => void
    updateWindowData: (id: string, data: Record<string, unknown>) => void
    stageBack: () => void
}

export type WindowManagerStore = WindowManagerState & WindowManagerActions

export interface DocumentWindowData {
    documentId?: string
    document?: {
        id: string
        title: string
        content: string
        markdownContent?: string
        type: string
        category?: string
        documentType?: string
        lastModified?: Date
        size?: string
        fileIds?: string[]
        childIds?: string[]
    }
}

interface DiagramViewerWindowData {
    diagramData: string
}

interface ReportWindowData {
    referenceNumber: string
}

interface FreightDashboardWindowData {
    mode?: (typeof FREIGHT_DASHBOARD_MODE_TABS)[number]
}

export interface SkillInstructionFocus {
    text: string
    headingPath: string[]
    nonce: number
}

interface SkillWindowData {
    slug: string
    focus?: SkillInstructionFocus
}

type TemplateWindowData =
    | { kind: "pdf"; docType: DocumentTemplateDocType }
    | { kind: "email"; templateKey: string }

export interface WindowProps {
    windowId: string
    data?: Record<string, unknown>
}

export function isDocumentWindowData(
    data: unknown
): data is DocumentWindowData {
    if (!isRecord(data)) return false
    if (data.document === undefined) return true
    if (!isRecord(data.document)) return false
    return (
        typeof data.document.id === "string" &&
        typeof data.document.title === "string"
    )
}

export function isDiagramViewerWindowData(
    data: unknown
): data is DiagramViewerWindowData {
    if (!isRecord(data)) return false
    return typeof data.diagramData === "string"
}

export function isReportWindowData(data: unknown): data is ReportWindowData {
    if (!isRecord(data)) return false
    return typeof data.referenceNumber === "string"
}

function isSkillInstructionFocus(
    focus: unknown
): focus is SkillInstructionFocus {
    if (!isRecord(focus)) return false
    return (
        typeof focus.text === "string" &&
        Array.isArray(focus.headingPath) &&
        focus.headingPath.every(heading => typeof heading === "string") &&
        typeof focus.nonce === "number"
    )
}

export function isSkillWindowData(data: unknown): data is SkillWindowData {
    if (!isRecord(data)) return false
    return (
        typeof data.slug === "string" &&
        data.slug.length > 0 &&
        (data.focus === undefined || isSkillInstructionFocus(data.focus))
    )
}

export function isFreightDashboardWindowData(
    data: unknown
): data is FreightDashboardWindowData {
    if (!isRecord(data)) return false
    if (data.mode === undefined) return true
    return (
        typeof data.mode === "string" &&
        (FREIGHT_DASHBOARD_MODE_TABS as readonly string[]).includes(data.mode)
    )
}

export function isTemplateWindowData(
    data: unknown
): data is TemplateWindowData {
    if (!isRecord(data)) return false
    if (data.kind === "pdf") {
        return (
            typeof data.docType === "string" &&
            (DOCUMENT_TEMPLATE_DOC_TYPES as readonly string[]).includes(
                data.docType
            )
        )
    }
    if (data.kind === "email") {
        return typeof data.templateKey === "string" && data.templateKey !== ""
    }
    return false
}
